import { DataGridPro, GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid-pro";
import { Button, styled } from "@mui/material";
import { Download as DownloadIcon, FileUpload as FileUploadIcon } from "@mui/icons-material";
import useInventoryUploads from "hooks/useInventoryUploads";
import { readableDate } from "utils/readableDate";
import useServerSideDataGridParams from "hooks/useServerSideDataGridParams";
import { useInterval } from "hooks/useInterval";
import { GridCellExpand } from "components/GridCellExpand";
import useDirectUpload from "hooks/useDirectUpload";
import { useRef, useState } from "react";
import { PageActions, PageBreadcrumbs, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { useAlert } from "hooks/useAlert";

/** Maximum CSV size for inventory uploads (1 MB). */
const MAX_INVENTORY_UPLOAD_FILE_BYTES = 1024 * 1024;

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  "& .MuiDataGrid-row:hover": {
    backgroundColor: theme.palette.mode == "dark" ? theme.palette.primary.dark : theme.palette.primary.light,
  },
}));

export default function InventoryUploadsDataGrid() {
  const defaultPageSize = 25;
  const { alertError } = useAlert();
  const { createInventoryUpload, getInventoryUploads, inventoryUploads, totalPages } = useInventoryUploads();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadSync } = useDirectUpload({
    keyPrefix: "inventory_uploads",
    useFilename: true,
  });

  useInterval(async () => {
    await getInventoryUploads();
  }, 5000);

  function renderCellExpand(params: GridRenderCellParams<string>) {
    return <GridCellExpand value={params.row.fileErrors || ""} width={params.colDef.computedWidth} />;
  }

  const columns: GridColDef[] = [
    {
      field: "created_at",
      headerName: "Created",
      width: 150,
      valueGetter: (params: GridValueGetterParams) => {
        return readableDate(params.row.createdAt);
      },
    },
    {
      field: "user.email",
      headerName: "Created By",
      width: 200,
      valueGetter: (params: GridValueGetterParams) => params.row.user.email,
    },
    {
      field: "total_rows",
      headerName: "Rows",
      width: 150,
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.totalRows;
      },
    },
    {
      field: "state",
      headerName: "Status",
      width: 150,
    },
    {
      field: "file_errors",
      headerName: "Errors",
      width: 500,
      sortable: false,
      renderCell: renderCellExpand,
    },
  ];

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > MAX_INVENTORY_UPLOAD_FILE_BYTES) {
        alertError("File is too large. Maximum size is 1 MB.");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      setIsUploading(true);
      try {
        const blob = await uploadSync(file);
        await createInventoryUpload(blob.signed_id);
        getInventoryUploads();
      } catch (error) {
        console.error("Upload failed:", error);
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const pageState = useServerSideDataGridParams(inventoryUploads);

  return (
    <>
      <PageBreadcrumbs links={[{ label: "Warehouse" }]} />
      <PageTitle>Inventory Uploads</PageTitle>
      <PageActions>
        <a
          href={process.env.PUBLIC_URL + "/inventory_upload_template.csv"}
          download
          style={{ textDecoration: "none", color: "#202020" }}
        >
          <Button variant="contained" color="inherit" startIcon={<DownloadIcon />}>
            Download CSV Template
          </Button>
        </a>
        <input ref={fileInputRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleFileUpload} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleButtonClick}
          disabled={isUploading}
          startIcon={<FileUploadIcon />}
        >
          {isUploading ? "Uploading..." : "Upload CSV Template"}
        </Button>
      </PageActions>
      <FullPagePaperLayout>
        <StyledDataGrid
          disableSelectionOnClick
          rows={inventoryUploads}
          columns={columns}
          {...pageState}
          pageSize={pageState.pageSize || defaultPageSize}
          rowsPerPageOptions={[25, 50, 100]}
          rowCount={totalPages * (pageState.pageSize || defaultPageSize)}
          disableColumnFilter
        />
      </FullPagePaperLayout>
    </>
  );
}
